import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import { articles, categories } from '@/mocks/data';
import type { Article, Category } from '@/mocks/types';

const articleImages: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=A%20clean%20modern%20minimalist%20workspace%20scene%20with%20a%20smartphone%20displaying%20app%20interface%20on%20a%20warm%20wooden%20desk%2C%20soft%20natural%20light%20from%20window%2C%20cream%20and%20beige%20tones%2C%20editorial%20product%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=1200&height=600&seq=article-01-detail&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=A%20modern%20iPhone%20on%20a%20minimalist%20white%20desk%20with%20a%20coffee%20cup%2C%20bright%20natural%20window%20light%2C%20clean%20cream%20background%2C%20editorial%20product%20photography%2C%20warm%20ambient%20tones&width=1200&height=600&seq=article-02-detail&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=A%20futuristic%20AI%20concept%20visualization%20with%20glowing%20amber%20data%20nodes%20on%20a%20dark%20minimalist%20background%2C%20abstract%20network%20connections%2C%20clean%20tech%20aesthetic%2C%20warm%20golden%20accents&width=1200&height=600&seq=article-03-detail&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=A%20closeup%20of%20hands%20typing%20on%20a%20laptop%20keyboard%20with%20a%20phone%20nearby%2C%20warm%20cozy%20lighting%2C%20cream%20and%20beige%20color%20palette%2C%20minimalist%20workspace%2C%20soft%20shadows&width=1200&height=600&seq=article-04-detail&orientation=landscape',
  'article-05': 'https://readdy.ai/api/search-image?query=A%20clean%20shield%20icon%20on%20a%20smartphone%20screen%20with%20soft%20glow%2C%20representing%20security%20and%20protection%2C%20warm%20minimalist%20setting%2C%20cream%20background%2C%20professional%20tech%20photography&width=1200&height=600&seq=article-05-detail&orientation=landscape',
  'article-06': 'https://readdy.ai/api/search-image?query=A%20modern%20app%20update%20notification%20concept%20on%20a%20smartphone%20screen%2C%20clean%20minimalist%20design%2C%20warm%20amber%20accents%2C%20soft%20natural%20light%2C%20cream%20background%2C%20professional%20UI%20mockup&width=1200&height=600&seq=article-06-detail&orientation=landscape',
  'article-07': 'https://readdy.ai/api/search-image?query=A%20minimalist%20privacy%20lock%20concept%20on%20a%20smartphone%2C%20soft%20golden%20glow%2C%20warm%20beige%20background%2C%20clean%20tech%20design%2C%20professional%20product%20photography%2C%20elegant%20shadows&width=1200&height=600&seq=article-07-detail&orientation=landscape',
  'article-08': 'https://readdy.ai/api/search-image?query=Multiple%20devices%20phone%20tablet%20laptop%20displayed%20on%20a%20clean%20wooden%20desk%2C%20warm%20natural%20light%2C%20cream%20minimalist%20background%2C%20synchronization%20concept%2C%20professional%20tech%20photography&width=1200&height=600&seq=article-08-detail&orientation=landscape',
  'article-09': 'https://readdy.ai/api/search-image?query=A%20premium%20golden%20membership%20card%20on%20a%20warm%20minimalist%20background%2C%20amber%20accents%2C%20soft%20lighting%2C%20clean%20elegant%20design%2C%20professional%20product%20photography%2C%20luxurious%20feel&width=1200&height=600&seq=article-09-detail&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=A%20wifi%20signal%20icon%20glowing%20above%20a%20smartphone%20on%20a%20minimalist%20desk%2C%20warm%20lighting%2C%20cream%20background%2C%20clean%20tech%20aesthetic%2C%20connection%20concept%2C%20professional%20photography&width=1200&height=600&seq=article-10-detail&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=A%20smartphone%20displaying%20dark%20mode%20interface%20in%20a%20dimly%20lit%20cozy%20room%2C%20warm%20ambient%20light%2C%20minimalist%20setting%2C%20comfortable%20atmosphere%2C%20professional%20tech%20photography&width=1200&height=600&seq=article-11-detail&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=A%20beginner%20friendly%20smartphone%20tutorial%20concept%20with%20welcome%20screen%20on%20phone%2C%20warm%20inviting%20atmosphere%2C%20cream%20background%2C%20soft%20natural%20light%2C%20clean%20minimalist%20design&width=1200&height=600&seq=article-12-detail&orientation=landscape',
};

function renderMarkdown(content: string) {
  return content.split('\n').map((line, i) => {
    if (line.startsWith('## ')) {
      return (
        <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
    }
    if (line.startsWith('### ')) {
      return (
        <h3 key={i} className="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
    }
    if (line.startsWith('**') && line.endsWith('**')) {
      return (
        <h4 key={i} className="font-heading text-base font-semibold text-foreground-900 mt-6 mb-2">
          {line.replace(/\*\*/g, '')}
        </h4>
      );
    }
    if (/^\d+\.\s/.test(line)) {
      return (
        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed ml-5 mb-1 list-decimal">
          {line.replace(/^\d+\.\s/, '')}
        </li>
      );
    }
    if (line.startsWith('- ')) {
      return (
        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed ml-5 mb-1 list-disc">
          {line.replace('- ', '')}
        </li>
      );
    }
    if (line.trim() === '') {
      return <div key={i} className="h-3"></div>;
    }
    return (
      <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
        {line}
      </p>
    );
  });
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article: Article | undefined = articles.find((a) => a.slug === slug);
  const category: Category | undefined = article ? categories.find((c) => c.slug === article.category) : undefined;

  const relatedArticles: Article[] = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kuaimaoapp.com/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://kuaimaoapp.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://kuaimaoapp.com/article/${article.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Organization', name: '快猫app' },
    publisher: { '@type': 'Organization', name: '快猫app' },
    image: articleImages[article.imageSeq],
    url: `https://kuaimaoapp.com/article/${article.slug}`,
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />

        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {category && (
              <>
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {category?.name || article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}阅读</span>
              <span><i className="ri-eye-line mr-1"></i>1.2万 次浏览</span>
            </div>
          </div>

          <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={articleImages[article.imageSeq]}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {renderMarkdown(article.content)}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={articleImages[rel.imageSeq]}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors duration-300">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}